<template>
  <div v-if="open">
    <input
      ref="inputRef"
      :placeholder="t('placeholderInput')"
      maxlength="50"
      :value="filterText"
      :disabled="loading"
      class="w-full border-b border-neutral-99 p-3 text-sm text-neutral-0 placeholder:text-neutral-40 focus:outline-none"
      @input="updateFilterText"
      @keydown.stop.escape="() => onToggle()"
      @keydown.stop.space
      @blur="focusInput"
    />
    <div v-if="!loading && filteredTags.length > 0" class="max-h-[40vh] overflow-y-auto">
      <ListboxOption
        v-for="tag in filteredTags"
        :key="tag.id"
        :value="tag"
        as="template"
        v-slot="{ active }"
      >
        <div 
          :class="[
            'flex cursor-pointer items-center space-x-3 py-2 pl-2',
            active ? 'bg-primary-95' : 'hover:bg-primary-95'
          ]"
        >
          <CheckBox
            class="pointer-events-none focus:outline-none focus:ring-0 focus:ring-offset-0 border-neutral-70 text-primary-40"
            :model-value="isTagSelected(tag)"
          />
          <svg
            :style="{
              color: `${tag.color}`,
            }"
            class="h-3 w-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 8 8"
          >
            <circle cx="4" cy="4" r="3" />
          </svg>
          <span 
            :class="[
              'truncate text-sm font-medium',
              isTagSelected(tag) ? 'text-neutral-40' : 'text-neutral-0'
            ]"
          >
            {{ tag.name }}
          </span>
        </div>
      </ListboxOption>
    </div>
    <SkeletonOptions v-else-if="loading" />
    <ListboxOption
      v-else-if="filterText.length < 2 || !allowCreate"
      disabled
      class="py-2 text-center text-sm font-medium text-neutral-40"
    >
      {{ t("noTag") }}
    </ListboxOption>
    <ListboxOption
      v-if="allowCreate && filterText.length > 1 && !perfectMatchFilterText"
      v-slot="{ active }"
      :value="{ createTag: filterText }"
      class="p-3"
    >
      <Button theme="white" class="w-full" :selected="active">
        <template #leading> <PlusSmIcon class="h-5 w-5 text-neutral-40" /> </template>
        <span class="truncate">{{ t("buttonCreateTag", { filterText }) }} </span>
      </Button>
    </ListboxOption>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect, watch } from "vue";

// Components
import SkeletonOptions from "./TagsOptionsSkeleton.vue";
import Button from "@atoms/SimpleButton.vue";
import CheckBox from "@atoms/CheckBox.vue";
import { ListboxOption } from "@headlessui/vue";

// Icons
import { PlusSmIcon } from "@heroicons/vue/solid";

// Utils
import { formatTextToSearch } from "@helpers/formatters";

// I18n
import { useI18n } from "vue-i18n";

// Types
import type { Tags } from "@/vue/types/tag";

const { t } = useI18n();

// Props
interface Props {
  selectedTags: Tags;
  tags: Tags;
  open: boolean;
  loading?: boolean;
  allowCreate?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedTags: () => [],
  tags: () => [],
  open: false,
  loading: false,
  allowCreate: true,
});

// Emits
const emit = defineEmits<{
  (e: "createTag", name: string): void;
  (e: "toggle"): void;
}>();

// Filter Tags
const filterText = ref("");
const clearFilterText = () => (filterText.value = "");
const updateFilterText = (ev: Event) => {
  filterText.value = (ev.target as HTMLInputElement).value;
};

const onToggle = () => {
  clearFilterText();
  emit("toggle");
};

// Filtered Tags
const filteredTags = computed<Tags>(() => {
  if (filterText.value.length === 0) return props.tags;
  return props.tags.filter((tag) => formatTextToSearch(tag.name).includes(formatTextToSearch(filterText.value)));
});

const perfectMatchFilterText = computed(() => {
  return filteredTags.value.some(
    (tag) =>
      tag.name.toLowerCase().replaceAll(/\s+/g, " ").trim() ===
      filterText.value.toLowerCase().replaceAll(/\s+/g, " ").trim(),
  );
});

// Computed para obtener un mapa de tags seleccionados (optimización de rendimiento)
const selectedTagsMap = computed(() => {
  const map = new Map();
  props.selectedTags.forEach(tag => map.set(tag.id, true));
  return map;
});

// Helper para verificar si un tag está seleccionado
const isTagSelected = (tag: any) => {
  return selectedTagsMap.value.has(tag.id);
};

// Focus Handle
const inputRef = ref();

watch(
  () => props.open,
  () => {
    clearFilterText();
  },
);

const focusInput = () => {
  if (!inputRef.value || !props.open) return;
  inputRef.value?.focus();
};

watchEffect(() => {
  focusInput();
});
</script>

<i18n lang="jsonc">
{
  "es": {
    "placeholderInput": "Buscar etiquetas...",
    "buttonCreateTag": "Crear etiqueta: \"{ filterText }\"",
    "noTag": "Sin etiquetas"
  },
  "pt": {
    "placeholderInput": "Pesquisar etiqueta...",
    "buttonCreateTag": "Criar etiqueta: \"{ filterText }\"",
    "noTag": "Sem tags"
  }
}
</i18n>
